import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { Subscription } from 'rxjs';
import { AGUAppLocation, AGUAppModuleAccent, AGUTabStripTab } from 'agu_app_shell';
import { RA_MODULE_KEYS } from '../../ra-module-keys';
import { ExplorerRaHostAdapter } from '../../ra-explorer-host.adapter';
import { RaCompositionState } from '../../ra-composition.state';
import { ResearchApiService } from '../../services/research-api.service';
import { ItemKind, ResearchGrantMatch, ResearchJobMatch, ResearchPersonMatch } from '../../models/research.models';
import { FormatJobMeta } from '../job-finder/job-card.component';
import { PersonRoleLine } from '../people-finder/person-card.component';
import { PickAccents } from '../dashboard/ra-dashboard.component';
import { FormatGrantMeta } from '../shared/item-meta';
import { DescribeError } from '../shared/describe-error';
import { LoadFailures } from '../shared/load-failures';
import { ListErrorCopy, SEARCH_EMPTY_COPY, STATE_NOUN, StatePanelCopy } from '../shared/state-copy';
import { PortalConfigService } from '../../services/portal-config.service';
import { NextResultsPanelId } from '../shared/panel-id';
import { InterleaveByRank } from './interleave';

/** The tabs this page offers, mirroring `Search.txt` ("All" / "Grants" / "Collaborators" / "Jobs"). */
export type SearchTabKey = 'all' | 'grant' | 'person' | 'job';

/** The three item kinds a row can be — `SearchTabKey` minus its 'all' tab. */
type SearchKindKey = Exclude<SearchTabKey, 'all'>;

const SEARCH_TAB_KEYS: readonly SearchTabKey[] = ['all', 'grant', 'person', 'job'];

function IsSearchTabKey(value: string): value is SearchTabKey {
    return (SEARCH_TAB_KEYS as readonly string[]).includes(value);
}

/** "Grant" / "Person" / "Job" — the row's kind eyebrow (Search.txt's `label()`). */
const KIND_LABEL: Readonly<Record<SearchKindKey, string>> = {
    grant: 'Grant',
    person: 'Person',
    job: 'Job',
};

/** Rows shown in the All tab: `researchSearchAll(q, ALL_TAB_PER_KIND)`, one small slice per kind. */
const ALL_TAB_PER_KIND = 5;
/** Rows shown once a single kind tab is selected: that kind's own dedicated search, over-fetched relative to the All tab's slice. */
const KIND_TAB_COUNT = 20;

interface SearchRowBase {
    key: string;
    title: string;
    metaLine: string;
    score: number;
}

export interface SearchGrantRow extends SearchRowBase {
    kind: 'grant';
    grant: ResearchGrantMatch;
}

export interface SearchPersonRow extends SearchRowBase {
    kind: 'person';
    person: ResearchPersonMatch;
}

export interface SearchJobRow extends SearchRowBase {
    kind: 'job';
    job: ResearchJobMatch;
}

export type SearchRow = SearchGrantRow | SearchPersonRow | SearchJobRow;

export function IsGrantRow(row: SearchRow): row is SearchGrantRow {
    return row.kind === 'grant';
}

export function IsPersonRow(row: SearchRow): row is SearchPersonRow {
    return row.kind === 'person';
}

export function IsJobRow(row: SearchRow): row is SearchJobRow {
    return row.kind === 'job';
}

function BuildGrantRows(grants: readonly ResearchGrantMatch[]): SearchGrantRow[] {
    return grants.map((grant) => ({ kind: 'grant', key: grant.contentID, title: grant.title, metaLine: FormatGrantMeta(grant), score: grant.score, grant }));
}

function BuildPersonRows(people: readonly ResearchPersonMatch[]): SearchPersonRow[] {
    return people.map((person) => ({ kind: 'person', key: person.contributorID, title: person.name, metaLine: PersonRoleLine(person), score: person.score, person }));
}

function BuildJobRows(jobs: readonly ResearchJobMatch[]): SearchJobRow[] {
    return jobs.map((job) => ({
        kind: 'job',
        key: job.jobId,
        title: job.title,
        metaLine: FormatJobMeta(job, { posted: 'compact' }),
        score: job.score,
        job,
    }));
}

/** Every kind tab's own result set + whether it has been upgraded from the All tab's shallow preview to its own dedicated, deeper search. */
interface KindState<TRow extends SearchRow> {
    rows: TRow[];
    /** False until this kind's own `SearchGrants`/`SearchPeople`/`SearchJobs` (count 20) has resolved — until then `rows` is only the `researchSearchAll` preview slice (count `ALL_TAB_PER_KIND`). */
    loaded: boolean;
}

function EmptyKindState<TRow extends SearchRow>(): KindState<TRow> {
    return { rows: [], loaded: false };
}

/**
 * Search everything (Task 24) — the sidebar search box's landing page.
 * Root component of the `search` module — rendered via `ngComponentOutlet`
 * with no inputs, so it reads its query the same way every other module
 * reads its `RecordID` (`JobFinderPageComponent`/`PeopleFinderPageComponent`
 * et al.'s `OnLocationChanged`): `RaSurfaceComponent.OpenSearch(query)` calls
 * `OpenModule('search', 'results', query)`, which lands here as
 * `location.RecordID`. Submitting a NEW query from this page's own search
 * box round-trips through the same `NavigateTo` call (rather than calling
 * `Search()` directly) so there is exactly one path from "a query is
 * entered" to "a search runs", and browser Back returns to the previous
 * query, same as every other finder page's search history.
 *
 * The All tab and the three kind tabs are deliberately different data
 * sources (brief, Task 24): All is one `researchSearchAll(q, 5)` call, a
 * small preview of each kind interleaved by rank (grants → collaborators → jobs); a kind tab is that one
 * kind's own dedicated search at `count: 20`, fetched lazily the first time
 * the tab is opened and cached in `KindState.loaded` so re-clicking a tab
 * already visited doesn't refetch.
 *
 * Product states (PD §5): 3 skeleton cards while loading, the error panel ("We could not load results", P/proto-states.js:115)
 * and "No results for that search" (:101). No truthfulness edit applies to this screen's copy.
 */
@Component({
    standalone: false,
    selector: 'ra-search',
    templateUrl: './search-page.component.html',
    styleUrl: './search-page.component.scss',
    // The mockup's `.main` is a full-bleed track with 32px of gutter, not a
    // centred 1200px column (search.html shared block :49).
    host: { class: 'ra-fullwidth' },
})
export class SearchPageComponent implements OnInit, OnDestroy {
    /** The results region this page's tab strip drives, unique per instance so two RA tabs never collide on `aria-controls` (review M-9). */
    readonly ResultsPanelId = NextResultsPanelId('search');

    /** Bound so the template can narrow `SearchRow` via `@if`. */
    readonly IsGrantRow = IsGrantRow;
    readonly IsPersonRow = IsPersonRow;
    readonly IsJobRow = IsJobRow;
    readonly KindLabel = KIND_LABEL;
    /** Each kind's colour, resolved once from the module that owns it — the mockup's `C = { Grant:'var(--teal)', … }` (search.html:335), read from the module table instead of restated. */
    private readonly accents: Readonly<Record<ItemKind, AGUAppModuleAccent>>;

    /** The search box's current text — seeded from `location.RecordID`, editable independent of the last-searched query until the member submits again. */
    queryDraft = '';
    /** The query the page has actually searched for. Null before the first search resolves — distinct from "searched and found nothing", which drives a different empty state. */
    searchedQuery: string | null = null;
    /** The query of the last Search() call, successful or not — what Retry re-runs before any search has succeeded. */
    private attemptedQuery: string | null = null;

    activeTab: SearchTabKey = 'all';

    /** All tab's rows: the three kinds' `researchSearchAll` preview slices interleaved by rank. */
    allRows: SearchRow[] = [];

    grant: KindState<SearchGrantRow> = EmptyKindState();
    person: KindState<SearchPersonRow> = EmptyKindState();
    job: KindState<SearchJobRow> = EmptyKindState();

    loading = false;
    error: string | null = null;
    /** The error panel's foot, retry note and Contact support link (PD §5). */
    readonly failures = new LoadFailures();
    readonly EmptyCopy: StatePanelCopy = SEARCH_EMPTY_COPY;
    readonly ErrorCopy: StatePanelCopy = ListErrorCopy(STATE_NOUN.Search);

    private locationSub: Subscription | null = null;
    /** The last `RecordID` this page acted on — guards against re-running the exact same search twice for one `NavigateTo` (`Location$` only ever emits once per navigation, but a fresh subscribe-on-mount always replays the current value). */
    private lastRecordID: string | null = null;

    constructor(
        private readonly api: ResearchApiService,
        private readonly host: ExplorerRaHostAdapter,
        composition: RaCompositionState,
        /** The memoised portal config, for the error panel's Contact support address (PD §5); absent outside a surface. */
        @Optional() @Inject(PortalConfigService) private readonly portalConfig: PortalConfigService | null = null
    ) {
        this.accents = PickAccents((moduleKey) => composition.AccentOf(moduleKey));
    }

    /** The colour a row of `kind` paints with — drives the row's `[data-accent]`, which the stylesheet turns into its rail and eyebrow colour. */
    AccentFor(kind: ItemKind): AGUAppModuleAccent {
        return this.accents[kind];
    }

    ngOnInit(): void {
        void this.failures.ReadSupport(this.portalConfig);
        this.locationSub = this.host.Location$.subscribe((loc) => this.OnLocationChanged(loc));
    }

    ngOnDestroy(): void {
        this.locationSub?.unsubscribe();
    }

    private OnLocationChanged(loc: AGUAppLocation): void {
        if (loc.ModuleKey !== RA_MODULE_KEYS.Search) return;
        const recordID = loc.RecordID ?? null;
        if (recordID === this.lastRecordID) return;
        this.lastRecordID = recordID;

        const query = (recordID ?? '').trim();
        this.queryDraft = query;
        if (!query) {
            this.ResetResults();
            return;
        }
        void this.Search(query);
    }

    private async Search(query: string): Promise<void> {
        this.attemptedQuery = query;
        this.loading = true;
        this.error = null;
        try {
            const result = await this.api.SearchAll(query, ALL_TAB_PER_KIND);
            this.searchedQuery = query;
            this.grant = { rows: BuildGrantRows(result.grants), loaded: false };
            this.person = { rows: BuildPersonRows(result.people), loaded: false };
            this.job = { rows: BuildJobRows(result.jobs), loaded: false };
            this.allRows = InterleaveByRank<SearchRow>([this.grant.rows, this.person.rows, this.job.rows]);
            this.activeTab = 'all';
            this.failures.Reset();
        } catch (e) {
            this.error = DescribeError(e);
            this.failures.Record(e);
            // Re-submitting the same text must search again, not be swallowed as "already here" (I-10).
            if (this.lastRecordID !== null && this.lastRecordID.trim() === query) this.lastRecordID = null;
        } finally {
            this.loading = false;
        }
    }

    private ResetResults(): void {
        this.attemptedQuery = null;
        this.searchedQuery = null;
        this.allRows = [];
        this.grant = EmptyKindState();
        this.person = EmptyKindState();
        this.job = EmptyKindState();
        this.activeTab = 'all';
        this.error = null;
    }

    // ── Search box ───────────────────────────────────────────────────────

    OnQueryDraftChange(value: string): void {
        this.queryDraft = value;
    }

    /** The mockup's `.sfield` is a native input, so the page reads its value off the event rather than through an MJ search component's `ValueChange`. */
    OnQueryInput(event: Event): void {
        const input = event.target;
        if (input instanceof HTMLInputElement) this.queryDraft = input.value;
    }

    /** Enter, or the Search button — round-trips through `NavigateTo` rather than calling `Search()` directly (see class doc). */
    OnSubmit(): void {
        const q = this.queryDraft.trim();
        if (!q) {
            this.OnClearSearch();
            return;
        }
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Search, View: 'results', RecordID: q });
    }

    OnClearSearch(): void {
        this.queryDraft = '';
        this.ResetResults();
    }

    // ── Tabs ─────────────────────────────────────────────────────────────

    /**
     * Label only, no count: the mockup's tabs carry none (search.html:313-318),
     * and R-F18 rules that the mockup wins. A count here would also be a half
     * truth — a kind tab shows the All tab's five-row preview until that kind's
     * own deeper search has run. The honest total is the `.rescount` line under
     * the strip, which counts exactly what is on screen.
     */
    get Tabs(): AGUTabStripTab[] {
        return [
            { Key: 'all', Label: 'All' },
            { Key: 'grant', Label: 'Grants' },
            { Key: 'person', Label: 'People' },
            { Key: 'job', Label: 'Jobs' },
        ];
    }

    OnTabChange(key: string): void {
        if (!IsSearchTabKey(key)) return;
        this.activeTab = key;
        if (key === 'all' || !this.searchedQuery) return;
        if (key === 'grant' && !this.grant.loaded) void this.LoadGrantTab(this.searchedQuery);
        else if (key === 'person' && !this.person.loaded) void this.LoadPersonTab(this.searchedQuery);
        else if (key === 'job' && !this.job.loaded) void this.LoadJobTab(this.searchedQuery);
    }

    private async LoadGrantTab(query: string): Promise<void> {
        this.loading = true;
        this.error = null;
        try {
            const result = await this.api.SearchGrants({ query: { text: query }, count: KIND_TAB_COUNT });
            this.grant = { rows: BuildGrantRows(result.results).sort((a, b) => b.score - a.score), loaded: true };
            this.failures.Reset();
        } catch (e) {
            this.error = DescribeError(e);
            this.failures.Record(e);
        } finally {
            this.loading = false;
        }
    }

    private async LoadPersonTab(query: string): Promise<void> {
        this.loading = true;
        this.error = null;
        try {
            const result = await this.api.SearchPeople({ query: { text: query }, count: KIND_TAB_COUNT });
            this.person = { rows: BuildPersonRows(result.results).sort((a, b) => b.score - a.score), loaded: true };
            this.failures.Reset();
        } catch (e) {
            this.error = DescribeError(e);
            this.failures.Record(e);
        } finally {
            this.loading = false;
        }
    }

    private async LoadJobTab(query: string): Promise<void> {
        this.loading = true;
        this.error = null;
        try {
            const result = await this.api.SearchJobs({ query: { text: query }, count: KIND_TAB_COUNT });
            this.job = { rows: BuildJobRows(result.results).sort((a, b) => b.score - a.score), loaded: true };
            this.failures.Reset();
        } catch (e) {
            this.error = DescribeError(e);
            this.failures.Record(e);
        } finally {
            this.loading = false;
        }
    }

    Retry(): void {
        // A failed FIRST search leaves searchedQuery null (it is set on success), so retry what was attempted (I-10).
        if (this.searchedQuery === null) {
            if (this.attemptedQuery) void this.Search(this.attemptedQuery);
            return;
        }
        if (this.activeTab === 'all') void this.Search(this.searchedQuery);
        else this.OnTabChange(this.activeTab);
    }

    // ── Display ──────────────────────────────────────────────────────────

    get DisplayRows(): SearchRow[] {
        if (this.activeTab === 'grant') return this.grant.rows;
        if (this.activeTab === 'person') return this.person.rows;
        if (this.activeTab === 'job') return this.job.rows;
        return this.allRows;
    }

    get IsEmpty(): boolean {
        return this.searchedQuery !== null && this.DisplayRows.length === 0;
    }

    /** The empty panel's "Browse grants" / "Browse people" (P/proto-states.js:101): that finder's search view. */
    OnBrowse(moduleKey: typeof RA_MODULE_KEYS.Grants | typeof RA_MODULE_KEYS.People): void {
        this.host.NavigateTo({ ModuleKey: moduleKey, View: 'search' });
    }

    // ── Row navigation ──────────────────────────────────────────────────

    OnOpenRow(row: SearchRow): void {
        // These results (their query) are the crumb's origin: the detail reads "Back to Search" and returns here (PD §6).
        const from = this.host.CurrentLocation;
        if (IsGrantRow(row)) {
            this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Grants, View: 'detail', RecordID: row.grant.contentID, From: from });
        } else if (IsPersonRow(row)) {
            this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.People, View: 'profile', RecordID: row.person.contributorID, From: from });
        } else {
            this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Jobs, View: 'detail', RecordID: row.job.jobId, From: from });
        }
    }
}
